class ViewCountIndicator extends HTMLElement {
  constructor() {
    super();
    this.countElement = this.querySelector('.count');
    this.intervalId = null;
    this.currentValue = null;
    this.visibilityHandler = this.handleVisibilityChange.bind(this);

    this.settings = {
      minCount: this.parseInteger(this.dataset.minCount, 70),
      maxCount: this.parseInteger(this.dataset.maxCount, 267),
      driftMin: this.parseInteger(this.dataset.driftMin, 2),
      driftMax: this.parseInteger(this.dataset.driftMax, 9),
      updateInterval: this.parseInteger(this.dataset.updateInterval, 12000),
      locale: this.dataset.locale || document.documentElement.lang || 'es-CL',
      pageScope: this.dataset.pageScope || window.location.pathname || '/',
    };
  }

  connectedCallback() {
    if (!this.countElement) return;

    this.currentValue = this.getInitialValue();
    this.render();
    this.classList.add('is-visible');
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.start();
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.stop();
  }

  safeGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  }

  safeSet(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (_error) {}
  }

  parseInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  getSessionKey() {
    return `brn-view-count:${this.settings.pageScope}`;
  }

  getBucketKey() {
    const now = new Date();
    const hourBucket = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}-${now.getHours()}`;
    return `${this.getSessionKey()}:${hourBucket}`;
  }

  getInitialValue() {
    const persisted = this.safeGet(this.getBucketKey());
    if (persisted) {
      const parsed = Number.parseInt(persisted, 10);
      if (Number.isFinite(parsed)) {
        return this.clamp(parsed);
      }
    }

    const seeded = this.seed(`${this.settings.pageScope}|${new Date().toDateString()}`);
    const spread = this.settings.maxCount - this.settings.minCount;
    const value = this.settings.minCount + Math.round(seeded * spread);

    this.persist(value);
    return this.clamp(value);
  }

  seed(input) {
    let hash = 2166136261;

    for (let index = 0; index < input.length; index += 1) {
      hash ^= input.charCodeAt(index);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }

    return Math.abs(hash >>> 0) / 4294967295;
  }

  clamp(value) {
    return Math.min(this.settings.maxCount, Math.max(this.settings.minCount, value));
  }

  persist(value) {
    this.safeSet(this.getBucketKey(), String(this.clamp(value)));
  }

  getNextValue() {
    const driftWindow = Math.max(this.settings.driftMin, this.settings.driftMax);
    const driftFloor = Math.min(this.settings.driftMin, this.settings.driftMax);
    const delta = Math.floor(Math.random() * (driftWindow - driftFloor + 1)) + driftFloor;
    const direction = Math.random() > 0.38 ? 1 : -1;

    return this.clamp(this.currentValue + direction * delta);
  }

  render() {
    this.countElement.textContent = new Intl.NumberFormat(this.settings.locale).format(this.currentValue);
  }

  tick() {
    if (document.hidden) return;

    this.currentValue = this.getNextValue();
    this.persist(this.currentValue);
    this.render();
  }

  start() {
    this.stop();

    this.intervalId = window.setInterval(() => {
      this.tick();
    }, this.settings.updateInterval);
  }

  stop() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.stop();
      return;
    }

    this.tick();
    this.start();
  }
}

if (!customElements.get('view-count-indicator')) {
  customElements.define('view-count-indicator', ViewCountIndicator);
}